import Container from 'components/molecules/Container/Container';
import { HelmetAndPageAnnouncer } from 'components/organisms/HelmetAndPageAnnouncer/HelmetAndPageAnnouncer';
import {
  CognitiveKind,
  useStartCognitiveTestAssessmentMutation,
  useStartCognitiveTestPracticeAssessmentMutation,
} from 'generated/graphql';
import { useWindowSize } from 'hooks/containerSize';
import { useDisableCopyAndRightClick } from 'hooks/useDisableCopyAndRightClick';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import InductiveTest from 'views/Test/Tests/InductiveTest/InductiveTest';
import DesktopNumericalTest from 'views/Test/Tests/NumericalTest/DesktopNumericalTest/DesktopNumericalTest';
import MobileNumericalTest from 'views/Test/Tests/NumericalTest/MobileNumericalTest/MobileNumericalTest';
import DesktopVerbalTest from 'views/Test/Tests/VerbalTest/DesktopVerbalTest/DesktopVerbalTest';
import MobileVerbalTest from 'views/Test/Tests/VerbalTest/MobileVerbalTest/MobileVerbalTest';

interface ICognitiveTestRunner {
  actionCandidateId: string;
  kind: CognitiveKind;
  isPracticeTest: boolean;
  onFinishTest: () => void;
}

const MOBILE_BREAKPOINT = 970;

const pageTitleTranslationKeys: Record<
  Exclude<CognitiveKind, CognitiveKind.Inductive>,
  { name: string; practiceName: string }
> = {
  [CognitiveKind.Numerical]: {
    name: 'test.instructions.numerical.name',
    practiceName: 'test.instructions.numerical.practiceName',
  },
  [CognitiveKind.Verbal]: {
    name: 'test.instructions.verbal.name',
    practiceName: 'test.instructions.verbal.practiceName',
  },
};

const CognitiveTestRunner: FC<ICognitiveTestRunner> = ({
  actionCandidateId,
  kind,
  isPracticeTest,
  onFinishTest,
}) => {
  const { t } = useTranslation();
  const { handleMsgType } = useNotification();
  useDisableCopyAndRightClick();
  const [containerSize] = useWindowSize();

  const [startAssessmentMutation, startAssessmentResponse] =
    useStartCognitiveTestAssessmentMutation({
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          message: error?.message,
        });
      },
      variables: {
        id: actionCandidateId,
      },
    });

  const [startPracticeAssessmentMutation, startPracticeAssessmentResponse] =
    useStartCognitiveTestPracticeAssessmentMutation({
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          message: error?.message,
        });
      },
      variables: {
        id: actionCandidateId,
      },
    });

  useEffect(() => {
    if (isPracticeTest) {
      startPracticeAssessmentMutation();
    } else {
      startAssessmentMutation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading =
    startAssessmentResponse.loading || startPracticeAssessmentResponse.loading;
  const assessmentId = isPracticeTest
    ? startPracticeAssessmentResponse.data
        ?.startCognitiveTestPracticeAssessment.assessmentId
    : startAssessmentResponse.data?.startCognitiveTestAssessment.assessmentId;

  if (loading) {
    return (
      <Container className="page-loader-wrapper">
        <Loader variant="bubbles" />
      </Container>
    );
  }

  if (!assessmentId) {
    return null;
  }

  // The inductive test owns its page title, loader and mobile breakpoint.
  if (kind === CognitiveKind.Inductive) {
    return (
      <InductiveTest
        assessmentId={assessmentId}
        isPracticeTest={isPracticeTest}
        onFinishTest={onFinishTest}
      />
    );
  }

  const isMobileWidth = containerSize.width < MOBILE_BREAKPOINT;
  const pageTitleKeys = pageTitleTranslationKeys[kind];

  return (
    <>
      <HelmetAndPageAnnouncer
        pageTitle={t(
          isPracticeTest ? pageTitleKeys.practiceName : pageTitleKeys.name
        )}
      />
      {kind === CognitiveKind.Numerical ? (
        isMobileWidth ? (
          <MobileNumericalTest
            assessmentId={assessmentId}
            onFinishTest={onFinishTest}
          />
        ) : (
          <DesktopNumericalTest
            assessmentId={assessmentId}
            isPracticeTest={isPracticeTest}
            onFinishTest={onFinishTest}
          />
        )
      ) : isMobileWidth ? (
        <MobileVerbalTest
          assessmentId={assessmentId}
          onFinishTest={onFinishTest}
        />
      ) : (
        <DesktopVerbalTest
          assessmentId={assessmentId}
          onFinishTest={onFinishTest}
        />
      )}
    </>
  );
};

export default CognitiveTestRunner;
